import type { Condiciones } from '../../../api/ops/pagos';
import { aCentimos, dinero, textoDeCentimos } from '../../../logica/dinero';
import { FORMA_CALCULO, horas } from '../textos';

/**
 * Cómo se leen las condiciones de una política en la lista y en la ficha (4.2). Solo **escribe** lo que manda la API:
 * el texto para el cliente es `conditions_text` (de la versión guardada o de `previewPaymentPolicyTerms`, G278), nunca
 * uno compuesto aquí (D-004).
 */
const hay = (m: Condiciones['deposit']): boolean => m !== undefined && m.amount > 0;

export function alReservar(t: Condiciones): { titulo: string; sub: string } {
    const base = FORMA_CALCULO[t.calculation_basis][1];
    if (hay(t.deposit)) {
        return { titulo: `Señal de ${dinero(t.deposit)} ${base}`, sub: t.guarantee_required ? 'Y tarjeta de garantía' : 'Sin tarjeta de garantía' };
    }
    if (t.guarantee_required) {
        return { titulo: 'Tarjeta de garantía', sub: 'No se cobra al reservar' };
    }
    return { titulo: 'Sin señal', sub: 'No se cobra al reservar' };
}

export function siCancelaTarde(t: Condiciones): { titulo: string; sub: string } {
    const base = FORMA_CALCULO[t.calculation_basis][1];
    const plazo = t.late_cancellation_window_hours ?? 0;
    return {
        titulo: hay(t.no_show_fee) ? `No-show: ${dinero(t.no_show_fee)} ${base}` : 'No-show: nada',
        sub: hay(t.late_cancellation_fee) ? `Tardía: ${dinero(t.late_cancellation_fee)} con menos de ${horas(plazo)}` : plazo > 0 ? `Gratis hasta ${horas(plazo)} antes` : 'Sin cargo por cancelar tarde',
    };
}

/** Lo que se escribe en el editor (importes como texto, «12,50»). */
export interface Borrador {
    nombre: string;
    forma: Condiciones['calculation_basis'];
    garantia: boolean;
    senal: string;
    noShow: string;
    tardia: string;
    plazo: string;
    varios: NonNullable<Condiciones['multi_service_rule']>;
    nota: string;
}

export function borradorDe(nombre: string, t: Condiciones | null): Borrador {
    return {
        nombre,
        forma: t?.calculation_basis ?? 'per_person',
        garantia: t?.guarantee_required ?? false,
        senal: textoDeCentimos(t?.deposit?.amount, true),
        noShow: textoDeCentimos(t?.no_show_fee?.amount, true),
        tardia: textoDeCentimos(t?.late_cancellation_fee?.amount, true),
        plazo: t?.late_cancellation_window_hours === undefined || t.late_cancellation_window_hours === 0 ? '' : String(t.late_cancellation_window_hours),
        varios: t?.multi_service_rule ?? 'single_service',
        nota: Object.values(t?.custom_note ?? {})[0] ?? '',
    };
}

export interface ErroresBorrador {
    nombre?: string;
    senal?: string;
    noShow?: string;
    tardia?: string;
    plazo?: string;
}

/** Comprueba lo escrito (formato, no reglas de negocio: esas las valida la API, G280). */
export function erroresDe(b: Borrador, conNombre: boolean): ErroresBorrador {
    const e: ErroresBorrador = {};
    if (conNombre && b.nombre.trim() === '') {
        e.nombre = 'Escribe un nombre.';
    }
    const importe = (v: string) => v.trim() !== '' && aCentimos(v) === null;
    if (importe(b.senal)) {
        e.senal = 'Escribe un importe, por ejemplo 10 o 12,50.';
    }
    if (importe(b.noShow)) {
        e.noShow = 'Escribe un importe, por ejemplo 10 o 12,50.';
    }
    if (importe(b.tardia)) {
        e.tardia = 'Escribe un importe, por ejemplo 10 o 12,50.';
    }
    if (b.plazo.trim() !== '' && !/^\d{1,4}$/.test(b.plazo.trim())) {
        e.plazo = 'Escribe las horas en números.';
    }
    return e;
}

/** Las condiciones que se mandan a la API (con la moneda del negocio). */
export function condicionesDe(b: Borrador, moneda: string, conVarios: boolean, locale = 'es'): Condiciones {
    const m = (v: string) => ({ amount: aCentimos(v) ?? 0, currency: moneda });
    const nota = b.nota.trim();
    return {
        calculation_basis: b.forma,
        guarantee_required: b.garantia,
        deposit: m(b.senal),
        no_show_fee: m(b.noShow),
        late_cancellation_fee: m(b.tardia),
        late_cancellation_window_hours: b.plazo.trim() === '' ? 0 : Number(b.plazo.trim()),
        ...(conVarios ? { multi_service_rule: b.varios } : {}),
        ...(nota === '' ? {} : { custom_note: { [locale]: nota } }),
    };
}
